import api from '../utils/apiClient';

export interface BankAccountInfo {
  bankName: string;
  accountNumber: string;
  iban: string;
  accountHolderName: string;
  description?: string;
}

export interface WithdrawalRequest {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'cancelled';
  bankAccountInfo: BankAccountInfo;
  requestDate: string;
  processedDate?: string;
  processedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  adminNote?: string;
  rejectReason?: string;
  paymentReference?: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WithdrawalRequestsResponse {
  requests: WithdrawalRequest[];
  totalCount: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface WithdrawalStats {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  processingRequests?: number;
  completedRequests?: number;
  rejectedRequests: number;
  cancelledRequests?: number;
  totalAmount: number;
  pendingAmount: number;
  reservedAmount?: number;
  completedAmount?: number;
}

export interface UserWithdrawalFilters {
  search?: string;
  minAmount?: string;
  maxAmount?: string;
  bankName?: string;
  verified?: 'all' | 'verified' | 'unverified';
  fromDate?: string;
  toDate?: string;
}

class UserWithdrawalService {
  private baseURL = '/admin/wallet/withdrawal-requests';

  /**
   * Get all withdrawal requests
   */
  async getAllWithdrawalRequests(
    page: number = 1,
    limit: number = 20,
    status?: string,
    filters?: UserWithdrawalFilters
  ): Promise<{
    status: string;
    data: WithdrawalRequestsResponse;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (status) {
        params.append('status', status);
      }
      // ✅ فیلترها سمت سرور اعمال می‌شوند (قبلاً فقط روی صفحهٔ فعلی فیلتر می‌شد)
      if (filters?.search?.trim()) params.append('search', filters.search.trim());
      if (filters?.minAmount) params.append('minAmount', filters.minAmount);
      if (filters?.maxAmount) params.append('maxAmount', filters.maxAmount);
      if (filters?.bankName?.trim()) params.append('bankName', filters.bankName.trim());
      if (filters?.verified && filters.verified !== 'all') params.append('verified', filters.verified === 'verified' ? 'true' : 'false');
      if (filters?.fromDate) params.append('fromDate', filters.fromDate);
      if (filters?.toDate) params.append('toDate', filters.toDate);

      const response = await api.get(`${this.baseURL}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error getting withdrawal requests:', error);
      throw error;
    }
  }

  /**
   * Get withdrawal request details
   */
  async getWithdrawalRequestDetails(requestId: string): Promise<{
    status: string;
    data: WithdrawalRequest;
  }> {
    try {
      const response = await api.get(`${this.baseURL}/${requestId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting withdrawal request details:', error);
      throw error;
    }
  }

  /**
   * Approve withdrawal request
   */
  async approveWithdrawalRequest(
    requestId: string,
    adminNote?: string
  ): Promise<{
    status: string;
    message: string;
    data: any;
  }> {
    try {
      const response = await api.post(`${this.baseURL}/${requestId}/approve`, {
        adminNote
      });
      return response.data;
    } catch (error) {
      console.error('Error approving withdrawal request:', error);
      throw error;
    }
  }

  /**
   * Start processing withdrawal request (APPROVED -> PROCESSING)
   */
  async startProcessingWithdrawal(
    requestId: string,
    adminNote?: string,
    paymentReference?: string
  ): Promise<{
    status: string;
    message: string;
    data: any;
  }> {
    try {
      const response = await api.post(`${this.baseURL}/${requestId}/start-processing`, {
        adminNote,
        paymentReference
      });
      return response.data;
    } catch (error) {
      console.error('Error starting withdrawal processing:', error);
      throw error;
    }
  }

  /**
   * Complete withdrawal request (PROCESSING -> COMPLETED)
   */
  async completeWithdrawal(
    requestId: string,
    adminNote?: string,
    paymentReference?: string
  ): Promise<{
    status: string;
    message: string;
    data: any;
  }> {
    try {
      const response = await api.post(`${this.baseURL}/${requestId}/complete`, {
        adminNote,
        paymentReference
      });
      return response.data;
    } catch (error) {
      console.error('Error completing withdrawal:', error);
      throw error;
    }
  }

  /**
   * Reject withdrawal request
   */
  async rejectWithdrawalRequest(
    requestId: string,
    rejectReason: string,
    adminNote?: string
  ): Promise<{
    status: string;
    message: string;
    data: any;
  }> {
    try {
      const response = await api.post(`${this.baseURL}/${requestId}/reject`, {
        rejectReason,
        adminNote
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting withdrawal request:', error);
      throw error;
    }
  }

  /**
   * ناموفق/لغو کردن برداشتِ تأییدشده یا در حال پردازش (واریز بانکی انجام نشد) — پول به کیف پول کاربر برمی‌گردد
   */
  async failWithdrawal(
    requestId: string,
    reason: string,
    adminNote?: string
  ): Promise<{ status: string; message: string; data: any }> {
    try {
      const response = await api.post(`${this.baseURL}/${requestId}/fail`, { reason, adminNote });
      return response.data;
    } catch (error) {
      console.error('Error failing withdrawal request:', error);
      throw error;
    }
  }

  /**
   * Get withdrawal statistics
   */
  async getWithdrawalStatistics(): Promise<{
    status: string;
    data: WithdrawalStats;
  }> {
    try {
      const response = await api.get(`${this.baseURL}/statistics`);
      return response.data;
    } catch (error) {
      console.error('Error getting withdrawal statistics:', error);
      throw error;
    }
  }

  /**
   * Format withdrawal status for display
   */
  formatWithdrawalStatus(status: string): string {
    const statusMap: Record<string, string> = {
      pending: 'در انتظار',
      approved: 'تایید شده',
      rejected: 'رد شده',
      processing: 'در حال پردازش',
      completed: 'تکمیل شده',
      cancelled: 'لغو شده'
    };

    return statusMap[status] || status;
  }

  /**
   * Get withdrawal status color
   */
  getWithdrawalStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    const colorMap: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      processing: 'info',
      completed: 'success',
      cancelled: 'default'
    };

    return colorMap[status] || 'default';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return `${minutes} دقیقه پیش`;
      }
      return `${hours} ساعت پیش`;
    } else if (days === 1) {
      return 'دیروز';
    } else if (days < 7) {
      return `${days} روز پیش`;
    } else {
      return date.toLocaleDateString('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  /**
   * Format currency
   */
  formatCurrency(amount: number): string {
    return amount.toLocaleString('fa-IR');
  }
}

export const userWithdrawalService = new UserWithdrawalService(); 